import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Badge, Button, Pagination, Form, Spinner, Modal, Dropdown } from 'react-bootstrap';
import { FaSearch, FaEye, FaSignOutAlt, FaUser, FaBell, FaQuestionCircle, FaTimes } from 'react-icons/fa';
import WFBackofficeSidebar from '../../components/WFBackofficeSidebar';
import useDebounce from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';
import { notificationApi, searchApi, wfbackofficeApi } from '../../services/api';
import SearchResults from '../../components/SearchResults';
import './Dashboard.css';

const WFBackofficeDsoPartners = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  
  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  // --- End Notification State ---
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---
  
  // DSO Partners state
  const [loading, setLoading] = useState(true);
  const [dsoPartners, setDsoPartners] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 10;
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal state for partner details
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Set document title on mount
  useEffect(() => {
    document.title = 'Wishfin Backoffice - DSO Partners';
  }, []);

  // Fetch DSO partners
  useEffect(() => {
    const fetchDsoPartners = async () => {
      setLoading(true);
      try {
        // Use the WF Backoffice API to get DSO partners with lead counts
        const response = await wfbackofficeApi.getDsoPartners({
          skip: (currentPage - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          search: debouncedSearchTerm,
          status: statusFilter !== '' ? statusFilter : undefined
        });
        
        if (response && response.data) {
          setDsoPartners(response.data.items || []);
          setTotalPages(Math.ceil(response.data.total / PAGE_SIZE) || 1);
        }
      } catch (error) {
        console.error('Error fetching DSO partners:', error);
        // Fallback to mock data if API fails
        const mockDsoPartners = [
          {
            id: 1,
            name: "ABC Finance Partners",
            email: "abc@example.com",
            phone: "9876543210",
            status: "active",
            created_at: "2024-08-15",
            total_leads: 125,
            active_agents: 15
          },
          {
            id: 2,
            name: "XYZ Loan Services",
            email: "xyz@example.com",
            phone: "8765432109",
            status: "pending",
            created_at: "2024-07-20",
            total_leads: 87,
            active_agents: 8
          },
          {
            id: 3,
            name: "Horizon Finance",
            email: "horizon@example.com",
            phone: "7654321098",
            status: "active",
            created_at: "2024-09-10",
            total_leads: 210,
            active_agents: 22
          }
        ];
        setDsoPartners(mockDsoPartners);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchDsoPartners();
  }, [currentPage, debouncedSearchTerm, statusFilter]);

  // Handle partner modal open
  const handleViewPartner = async (partnerId) => {
    const partner = dsoPartners.find(p => p.id === partnerId);
    if (partner) {
      setSelectedPartner(partner);
      setShowPartnerModal(true);
      setLoadingDetails(true);
      
      try {
        // Fetch detailed information about the partner using the WFBackoffice API
        const response = await wfbackofficeApi.getDsoPartnerDetails(partnerId);
        setPartnerDetails(response.data);
      } catch (error) {
        console.error('Error fetching partner details:', error);
        // Use basic partner info as fallback if API call fails
        setPartnerDetails({
          ...partner,
          address: partner.address || 'N/A',
          city: partner.city || 'N/A', 
          state: partner.state || 'N/A',
          total_leads: partner.total_leads || 0,
          active_agents: partner.active_agents || 0,
          converted_leads: partner.converted_leads || 0,
          total_earnings: partner.total_earnings || 0
        });
      } finally {
        setLoadingDetails(false);
      }
    }
  };
  
  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    items.push(
      <Pagination.Prev 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      />
    );
    
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }
    
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item 
          key={page} 
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    items.push(
      <Pagination.Next 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      />
    );
    
    return items;
  };

  // Get badge color based on status
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // 300ms debounce
      
      setSearchTimeout(timeoutId);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  // Perform the search
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search result click
  const handleResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
    setSearchQuery('');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);

  // --- Notification useEffect Hooks ---
  // Fetch unread count periodically
  useEffect(() => {
    let intervalId;
    const fetchNotificationsCount = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsCount(); // Initial fetch
    intervalId = setInterval(fetchNotificationsCount, 60000); // Fetch every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  // Fetch full notifications when dropdown is opened
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          // Fetch latest 10 notifications
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };

    fetchFullNotifications();
  }, [showNotifications]);
  // --- End Notification useEffect Hooks ---
      
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // --- Notification Helper Functions ---
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return; // No unread notifications

    try {
      await notificationApi.markAllAsRead();
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0); // Reset count
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return timestamp;
    }
  };
  // --- End Notification Helper Functions ---

  return (
    <div className="backoffice-dashboard-layout">
      <WFBackofficeSidebar />
      
      <div className="backoffice-content-wrapper">
        <header className="backoffice-header">
          <div className="backoffice-greeting">
            <span className="backoffice-greeting-text">Hi, {currentUser?.name || 'Wishfin'} <span className="backoffice-greeting-emoji">👋</span></span>
          </div>
          <div className="backoffice-header-search" ref={searchRef}>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search partners..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
              {searchQuery && !isSearching && (
                <FaTimes 
                  className="search-clear-icon" 
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }} 
                />
              )}
            </div>
            
            {/* Desktop Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-container">
                <SearchResults results={searchResults} onResultClick={handleResultClick} />
              </div>
            )}
            
            {/* Mobile Search Button */}
            <div className="mobile-search-button" onClick={() => setShowSearchModal(true)}>
              <FaSearch />
            </div>
          </div>
          
          <div className="backoffice-header-actions">
            <div className="backoffice-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            {/* Notification Dropdown */}
            <Dropdown
              className="notification-dropdown backoffice-notifications"
              show={showNotifications}
              onToggle={(isOpen) => setShowNotifications(isOpen)}
              align="end"
            >
              <Dropdown.Toggle variant="link" id="dropdown-notifications" className="p-0 border-0 backoffice-header-icon">
                <FaBell size={18} />
                {unreadCount > 0 && (
                  <Badge pill bg="danger" className="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ minWidth: '320px', maxHeight: '450px', overflowY: 'auto', padding: '0' }}>
                <Dropdown.Header className="d-flex justify-content-between align-items-center sticky-top bg-light">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="btn btn-link btn-sm p-0" onClick={handleMarkAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </Dropdown.Header>
                {loadingNotifications ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <Dropdown.Item
                      key={notification.id}
                      className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="notification-content">
                          <div className="notification-title">{notification.title || notification.type}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-timestamp">{formatTimestamp(notification.created_at)}</div>
                        </div>
                        {!notification.is_read && <div className="unread-dot"></div>}
                      </div>
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-4">No new notifications</Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </Dropdown>
            <div className="backoffice-user-profile">
              <div className="backoffice-avatar">
                <FaUser />
              </div>
              <div className="backoffice-user-info">
                <span className="backoffice-user-name">
                  {currentUser?.name || 'Wishfin User'}
                </span>
                <button className="backoffice-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="backoffice-main-content">
          <div className="backoffice-dashboard-welcome">
            <h1>DSO Partner Management</h1>
            <p>View and manage Distribution Service Owners and their leads</p>
          </div>
          
          <div className="filter-container mb-4">
            <div className="row">
              <div className="col-md-4 mb-3 mb-md-0">
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search by name, phone, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <Form.Select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                </Form.Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading partners...</p>
            </div>
          ) : dsoPartners.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table striped hover className="backoffice-table">
                  <thead>
                    <tr>
                      <th>DSO ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Total Leads</th>
                      <th>Active Agents</th>
                      <th>Registration Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dsoPartners.map((partner) => (
                      <tr key={partner.id}>
                        <td>{partner.id}</td>
                        <td>{partner.name}</td>
                        <td>{partner.phone}</td>
                        <td>{partner.email}</td>
                        <td>
                          <Badge bg={getStatusBadge(partner.status)}>
                            {partner.status || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          <strong>{partner.total_leads || 0}</strong>
                        </td>
                        <td>
                          <strong>{partner.active_agents || 0}</strong>
                        </td>
                        <td>
                          {partner.created_at 
                            ? new Date(partner.created_at).toLocaleDateString() 
                            : 'N/A'}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewPartner(partner.id)}
                            className="action-btn"
                          >
                            <FaEye /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-between align-items-center mt-4 pagination-container">
                <div>
                  Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, dsoPartners.length)} to {Math.min(currentPage * PAGE_SIZE, dsoPartners.length)} of {totalPages * PAGE_SIZE} entries
                </div>
                <Pagination>{generatePaginationItems()}</Pagination>
              </div>
            </>
          ) : (
            <div className="text-center my-5">
              <p>No DSO partners found matching your criteria.</p>
              {(debouncedSearchTerm || statusFilter) && (
                <Button 
                  variant="link" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile Search Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search partners..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
            {searchQuery && !isSearching && (
              <FaTimes
                className="search-clear-icon"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  setSearchResults([]);
                }}
              />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mobile-search-results">
              <SearchResults results={searchResults} onResultClick={handleResultClick} />
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results-message">
              No results found for "{searchQuery}"
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Partner Details Modal */}
      <Modal 
        show={showPartnerModal} 
        onHide={() => setShowPartnerModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Partner Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : selectedPartner && partnerDetails ? (
            <div className="partner-details">
              <div className="row mb-4">
                <div className="col-md-3 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <h6>Total Leads</h6>
                      <h3>{partnerDetails.total_leads || 0}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <h6>Active Agents</h6>
                      <h3>{partnerDetails.active_agents || 0}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <h6>Converted Leads</h6>
                      <h3>{partnerDetails.converted_leads || 0}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <h6>Total Earnings</h6>
                      <h3>₹{partnerDetails.total_earnings?.toLocaleString() || 0}</h3>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <h5>Basic Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <th>DSO ID</th>
                        <td>{selectedPartner.id}</td>
                      </tr>
                      <tr>
                        <th>Name</th>
                        <td>{selectedPartner.name}</td>
                      </tr>
                      <tr>
                        <th>Phone</th>
                        <td>{selectedPartner.phone}</td>
                      </tr>
                      <tr>
                        <th>Email</th>
                        <td>{selectedPartner.email}</td>
                      </tr>
                      <tr>
                        <th>Status</th>
                        <td>
                          <Badge bg={getStatusBadge(selectedPartner.status)}>
                            {selectedPartner.status || 'Unknown'}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Registration Date</th>
                        <td>
                          {selectedPartner.created_at 
                            ? new Date(selectedPartner.created_at).toLocaleDateString() 
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5>Contact Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <th>Address</th>
                        <td>{partnerDetails.address || 'No address provided'}</td>
                      </tr>
                      <tr>
                        <th>City</th>
                        <td>{partnerDetails.city || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>State</th>
                        <td>{partnerDetails.state || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Pincode</th>
                        <td>{partnerDetails.pincode || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Alternative Contact</th>
                        <td>{partnerDetails.alternative_contact || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="mt-4">
                <h5>Verification Status</h5>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>Status</th>
                      <th>Verified On</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>PAN Card</td>
                      <td>
                        <Badge bg={partnerDetails.pan_verified ? 'success' : 'warning'}>
                          {partnerDetails.pan_verified ? 'Verified' : 'Pending'}
                        </Badge>
                      </td>
                      <td>
                        {partnerDetails.pan_verified_at 
                          ? new Date(partnerDetails.pan_verified_at).toLocaleDateString() 
                          : 'Not verified'}
                      </td>
                    </tr>
                    <tr>
                      <td>Aadhar Card</td>
                      <td>
                        <Badge bg={partnerDetails.aadhar_verified ? 'success' : 'warning'}>
                          {partnerDetails.aadhar_verified ? 'Verified' : 'Pending'}
                        </Badge>
                      </td>
                      <td>
                        {partnerDetails.aadhar_verified_at 
                          ? new Date(partnerDetails.aadhar_verified_at).toLocaleDateString() 
                          : 'Not verified'}
                      </td>
                    </tr>
                    <tr>
                      <td>Business Registration</td>
                      <td>
                        <Badge bg={partnerDetails.business_verified ? 'success' : 'warning'}>
                          {partnerDetails.business_verified ? 'Verified' : 'Pending'}
                        </Badge>
                      </td>
                      <td>
                        {partnerDetails.business_verified_at 
                          ? new Date(partnerDetails.business_verified_at).toLocaleDateString() 
                          : 'Not verified'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4">
                <h5>Lead Distribution</h5>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product Category</th>
                        <th>Total Leads</th>
                        <th>Converted</th>
                        <th>Conversion Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerDetails.lead_distribution ? (
                        Object.entries(partnerDetails.lead_distribution).map(([category, data], index) => (
                          <tr key={index}>
                            <td>{category}</td>
                            <td>{data.total || 0}</td>
                            <td>{data.converted || 0}</td>
                            <td>
                              {data.total > 0 
                                ? `${Math.round((data.converted / data.total) * 100)}%` 
                                : '0%'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">No lead distribution data available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center">No partner details available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPartnerModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            disabled={!selectedPartner}
            onClick={() => {
              // Handle view complete profile action
              if (selectedPartner) {
                // Navigate or perform action
                console.log(`View complete profile for partner ${selectedPartner.id}`);
                setShowPartnerModal(false);
              }
            }}
          >
            View Complete Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WFBackofficeDsoPartners;